import { vendorMatchesCustomTagFilter } from "./myVendorsTagMatcher";
import { collectVendorRiskRatings } from "./myVendorRiskUtils";
import { isAddedManuallyVendor, DOCUMENT_RISK_SCORE_FILTER_ENABLED } from "./myVendorsConstants";
import { resolveSelectedRfiServiceNames } from "./myVendorListsFilters";

function normalizeServiceKey(value = "") {
  return String(value).trim().toLowerCase().replace(/new$/i, "");
}

function normalizeSearch(value = "") {
  return String(value).trim().toLowerCase();
}

function vendorMatchesType(vendor, selectedTypeIds = [], providerTypes = []) {
  if (!selectedTypeIds.length) return true;
  const selectedLabels = new Set(
    providerTypes
      .filter((t) => selectedTypeIds.includes(t.id ?? t.value ?? t.name))
      .map((t) => t.name ?? t.label)
  );
  return selectedLabels.has(vendor.type);
}

function vendorMatchesPlatformManual(vendor, selected = []) {
  if (!selected.length) return true;
  // Hidden vendors live in their own section — keep them in the filtered set.
  if (vendor.isHidden) return true;
  const isManual = isAddedManuallyVendor(vendor);
  const isPlatform = !isManual;
  return (
    (selected.includes("requested-in-platform") && isPlatform) ||
    (selected.includes("added-manually") && isManual)
  );
}

function vendorMatchesRiskScores(vendor, selected = []) {
  if (!selected.length) return true;
  const ratings = collectVendorRiskRatings(vendor);
  return ratings.some((rating) => selected.includes(rating));
}

function vendorMatchesServices(vendor, selected = [], rfis = []) {
  if (!selected.length) return true;
  const wantedKeys = new Set(
    resolveSelectedRfiServiceNames(selected, rfis).map(normalizeServiceKey)
  );
  selected.forEach((id) => wantedKeys.add(normalizeServiceKey(id)));

  const profileServices = vendor.serviceCategories || vendor.services || [];
  const requestServices = [...(vendor.myRequests || []), ...(vendor.libraryRequests || [])].flatMap(
    (req) => req.services || []
  );
  const services = [...profileServices, ...requestServices];
  return services.some((service) => wantedKeys.has(normalizeServiceKey(service)));
}

function vendorMatchesGxps(vendor, selected = []) {
  if (!selected.length) return true;
  const profileGxps = [
    ...(vendor.applicableGxPs || []),
    ...(vendor.demonstratedGxPs || []),
    ...(vendor.gxps || []),
  ];
  const requestGxps = [...(vendor.myRequests || []), ...(vendor.libraryRequests || [])].flatMap(
    (req) => req.gxps || []
  );
  const gxps = [...profileGxps, ...requestGxps];
  return gxps.some((gxp) => selected.includes(gxp));
}

/** Yes / No bookmark filter — both selected (or neither) ≡ no filter. */
function vendorMatchesBookmarked(vendor, selected = [], isBookmarked) {
  if (!selected.length || selected.length >= 2 || typeof isBookmarked !== "function") {
    return true;
  }
  const marked = isBookmarked(vendor?.id);
  const wantsYes = selected.includes("yes");
  const wantsNo = selected.includes("no");
  return (wantsYes && marked) || (wantsNo && !marked);
}

/** Client-side filter pass for My Vendors list (search + toolbar filters). */
export function applyMyVendorsFilters(
  vendors = [],
  {
    searchQuery = "",
    selectedIds = {},
    providerTypes = [],
    isBookmarked,
    rfis = [],
  } = {}
) {
  const query = normalizeSearch(searchQuery);
  return vendors.filter((vendor) => {
    if (query && !normalizeSearch(vendor.name).includes(query)) {
      return false;
    }
    if (!vendorMatchesType(vendor, selectedIds.type, providerTypes)) return false;
    if (!vendorMatchesPlatformManual(vendor, selectedIds.platformManual)) return false;
    if (!vendorMatchesServices(vendor, selectedIds.services, rfis)) return false;
    if (!vendorMatchesGxps(vendor, selectedIds.gxp)) return false;
    if (DOCUMENT_RISK_SCORE_FILTER_ENABLED && !vendorMatchesRiskScores(vendor, selectedIds.riskScores)) {
      return false;
    }
    if (!vendorMatchesCustomTagFilter(vendor.tags, selectedIds.customTags)) return false;
    if (!vendorMatchesBookmarked(vendor, selectedIds.bookmarked, isBookmarked)) return false;
    return true;
  });
}
